import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Info } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { PORTAL_URL } from "@/config/intake";

// Clinical-trial endpoint % total body weight loss (mean), used as the
// asymptote for the projection. Sources: STEP-1 (semaglutide 2.4mg, 68w),
// SURMOUNT-1 (tirzepatide, 72w).
const MEDS = [
  { id: "sema-2.4", label: "Semaglutide 2.4 mg (Wegovy)", target: 0.149, weeks: 68 },
  { id: "sema-1.7", label: "Semaglutide 1.7 mg", target: 0.13, weeks: 68 },
  { id: "tirz-5", label: "Tirzepatide 5 mg", target: 0.15, weeks: 72 },
  { id: "tirz-10", label: "Tirzepatide 10 mg", target: 0.195, weeks: 72 },
  { id: "tirz-15", label: "Tirzepatide 15 mg (Zepbound)", target: 0.209, weeks: 72 },
] as const;

type MedId = (typeof MEDS)[number]["id"];

const UNITS = ["lb", "kg"] as const;
type Unit = (typeof UNITS)[number];

// Exponential approach to the trial-endpoint loss. k chosen so ~95% of the
// projected loss is reached by the trial endpoint week.
function projectedWeight(startWeight: number, target: number, weeks: number, t: number) {
  const k = -Math.log(0.05) / weeks;
  const lossFraction = target * (1 - Math.exp(-k * t));
  return startWeight * (1 - lossFraction);
}

function bmi(weightLb: number, heightIn: number) {
  if (!heightIn) return 0;
  return (weightLb / (heightIn * heightIn)) * 703;
}

function bmiClass(b: number) {
  if (b < 18.5) return "Underweight";
  if (b < 25) return "Healthy";
  if (b < 30) return "Overweight";
  if (b < 35) return "Obesity I";
  if (b < 40) return "Obesity II";
  return "Obesity III";
}

const CANONICAL = "https://olivehealth.care/tools/glp-1-calculator";

export default function Glp1Calculator() {
  const [unit, setUnit] = useState<Unit>("lb");
  const [weight, setWeight] = useState<number>(220);
  const [heightFt, setHeightFt] = useState<number>(5);
  const [heightIn, setHeightIn] = useState<number>(9);
  const [med, setMed] = useState<MedId>("tirz-15");

  useEffect(() => {
    const title = "GLP-1 Weight Loss Calculator — Olive Health";
    const description =
      "Estimate your weight loss on Semaglutide or Tirzepatide using STEP-1 and SURMOUNT-1 clinical-trial data. Free GLP-1 projection calculator from Olive Health.";
    document.title = title;

    const setMeta = (selector: string, attr: string, key: string, content: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta(`meta[name="description"]`, "name", "description", description);
    setMeta(`meta[property="og:title"]`, "property", "og:title", title);
    setMeta(`meta[property="og:description"]`, "property", "og:description", description);
    setMeta(`meta[property="og:url"]`, "property", "og:url", CANONICAL);
    setMeta(`meta[property="og:image"]`, "property", "og:image", `${"https://olivehealth.care"}/og/tools-glp-1-calculator.png`);
    setMeta(`meta[name="twitter:title"]`, "name", "twitter:title", title);
    setMeta(`meta[name="twitter:description"]`, "name", "twitter:description", description);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", CANONICAL);

    const ldId = "ld-glp1-calc";
    document.getElementById(ldId)?.remove();
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = ldId;
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "GLP-1 Weight Loss Calculator",
      url: CANONICAL,
      applicationCategory: "HealthApplication",
      operatingSystem: "Any",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      provider: { "@type": "MedicalBusiness", name: "Olive Health" },
    });
    document.head.appendChild(s);
  }, []);

  const medInfo = MEDS.find((m) => m.id === med)!;

  // Normalize to lb for the math, height to inches.
  const startLb = unit === "lb" ? weight : weight * 2.20462;
  const heightInches = heightFt * 12 + heightIn;
  const currentBmi = bmi(startLb, heightInches);

  const curve = useMemo(() => {
    const pts = [];
    const horizon = medInfo.weeks;
    for (let w = 0; w <= horizon; w += 2) {
      const lb = projectedWeight(startLb, medInfo.target, medInfo.weeks, w);
      pts.push({ w, lb });
    }
    return pts;
  }, [startLb, medInfo]);

  const endpointLb = projectedWeight(startLb, medInfo.target, medInfo.weeks, medInfo.weeks);
  const lostLb = startLb - endpointLb;
  const endpointDisplay = unit === "lb" ? endpointLb : endpointLb / 2.20462;
  const lostDisplay = unit === "lb" ? lostLb : lostLb / 2.20462;
  const endpointBmi = bmi(endpointLb, heightInches);

  const milestones = [12, 24, 52, medInfo.weeks].filter((w, i, a) => a.indexOf(w) === i);

  // SVG chart geometry
  const W = 720;
  const H = 280;
  const PAD = { l: 48, r: 16, t: 16, b: 32 };
  const maxLb = startLb;
  const minLb = endpointLb;
  const xScale = (w: number) => PAD.l + (w / medInfo.weeks) * (W - PAD.l - PAD.r);
  const yScale = (lb: number) =>
    PAD.t + (1 - (lb - minLb) / Math.max(1, maxLb - minLb)) * (H - PAD.t - PAD.b);
  const path = curve
    .map((p, i) => `${i === 0 ? "M" : "L"}${xScale(p.w).toFixed(1)},${yScale(p.lb).toFixed(1)}`)
    .join(" ");
  const area = `${path} L${xScale(medInfo.weeks).toFixed(1)},${(H - PAD.b).toFixed(1)} L${xScale(0).toFixed(1)},${(H - PAD.b).toFixed(1)} Z`;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main>
        <section className="container-wide pt-28 pb-12 md:pt-36 md:pb-16">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-foreground/60 mb-4">
              Free clinical tool
            </p>
            <h1 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight">
              GLP-1 weight loss calculator
            </h1>
            <p className="mt-5 text-lg text-foreground/70 max-w-2xl leading-relaxed">
              Project your trajectory on Semaglutide or Tirzepatide using mean results from the
              STEP-1 and SURMOUNT-1 clinical trials. Estimates only — your clinician personalizes
              the plan.
            </p>
          </motion.div>
        </section>

        <section className="container-wide pb-24">
          <div className="grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2 rounded-2xl border border-border/60 bg-card p-6 md:p-8">
              <h2 className="font-display text-xl mb-6">Your details</h2>

              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="weight" className="text-sm font-medium">
                      Starting weight
                    </label>
                    <div className="inline-flex rounded-full border border-border/60 p-0.5 text-xs">
                      {UNITS.map((u) => (
                        <button
                          key={u}
                          type="button"
                          onClick={() => setUnit(u)}
                          className={`px-3 py-1 rounded-full transition ${
                            unit === u
                              ? "bg-foreground text-background"
                              : "text-foreground/70 hover:text-foreground"
                          }`}
                          aria-pressed={unit === u}
                          aria-label={u === "lb" ? "Use pounds" : "Use kilograms"}
                        >
                          {u}
                        </button>
                      ))}
                    </div>
                  </div>
                  <input
                    id="weight"
                    type="number"
                    inputMode="decimal"
                    value={Number.isFinite(weight) ? weight : ""}
                    onChange={(e) => setWeight(Number(e.target.value))}
                    min={50}
                    max={unit === "lb" ? 700 : 320}
                    className="w-full rounded-xl border border-border/60 bg-background px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-accent/40"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium block mb-2">Height</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <input
                        type="number"
                        value={heightFt}
                        onChange={(e) => setHeightFt(Number(e.target.value))}
                        min={3}
                        max={8}
                        aria-label="Height feet"
                        className="w-full rounded-xl border border-border/60 bg-background px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-accent/40"
                      />
                      <p className="text-xs text-foreground/60 mt-1">feet</p>
                    </div>
                    <div>
                      <input
                        type="number"
                        value={heightIn}
                        onChange={(e) => setHeightIn(Number(e.target.value))}
                        min={0}
                        max={11}
                        aria-label="Height inches"
                        className="w-full rounded-xl border border-border/60 bg-background px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-accent/40"
                      />
                      <p className="text-xs text-foreground/60 mt-1">inches</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="med" className="text-sm font-medium block mb-2">
                    Medication
                  </label>
                  <select
                    id="med"
                    value={med}
                    onChange={(e) => setMed(e.target.value as MedId)}
                    className="w-full rounded-xl border border-border/60 bg-background px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-accent/40"
                  >
                    {MEDS.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rounded-xl bg-muted/40 p-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-foreground/65">Current BMI</span>
                    <span className="font-medium">
                      {currentBmi.toFixed(1)} · {bmiClass(currentBmi)}
                    </span>
                  </div>
                  <div className="flex justify-between mt-1.5">
                    <span className="text-foreground/65">Projected BMI</span>
                    <span className="font-medium">
                      {endpointBmi.toFixed(1)} · {bmiClass(endpointBmi)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3 rounded-2xl border border-border/60 bg-card p-6 md:p-8">
              <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div>
                  <h2 className="font-display text-xl">Projected trajectory</h2>
                  <p className="text-sm text-foreground/65 mt-1">
                    Based on {medInfo.label.split(" (")[0]} trial endpoint at week {medInfo.weeks}.
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-[0.18em] text-foreground/60">
                    Estimated loss
                  </p>
                  <p className="font-display text-3xl mt-1">
                    {lostDisplay.toFixed(1)} {unit}
                  </p>
                  <p className="text-sm text-foreground/65">
                    ({((lostLb / startLb) * 100).toFixed(1)}% of body weight)
                  </p>
                </div>
              </div>

              <div className="w-full overflow-x-auto">
                <svg
                  viewBox={`0 0 ${W} ${H}`}
                  role="img"
                  aria-label="Projected weight trajectory"
                  className="w-full h-auto"
                >
                  <defs>
                    <linearGradient id="curveFill" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  {[0, 0.25, 0.5, 0.75, 1].map((t) => {
                    const y = PAD.t + t * (H - PAD.t - PAD.b);
                    const lb = maxLb - t * (maxLb - minLb);
                    return (
                      <g key={t}>
                        <line
                          x1={PAD.l}
                          x2={W - PAD.r}
                          y1={y}
                          y2={y}
                          stroke="hsl(var(--border))"
                          strokeDasharray="2 4"
                        />
                        <text
                          x={PAD.l - 8}
                          y={y + 4}
                          textAnchor="end"
                          fontSize="11"
                          fill="hsl(var(--muted-foreground))"
                        >
                          {(unit === "lb" ? lb : lb / 2.20462).toFixed(0)}
                        </text>
                      </g>
                    );
                  })}
                  {[0, 12, 24, 36, 48, 60, 72].filter((w) => w <= medInfo.weeks).map((w) => (
                    <text
                      key={w}
                      x={xScale(w)}
                      y={H - PAD.b + 18}
                      textAnchor="middle"
                      fontSize="11"
                      fill="hsl(var(--muted-foreground))"
                    >
                      {w}w
                    </text>
                  ))}
                  <path d={area} fill="url(#curveFill)" />
                  <path d={path} fill="none" stroke="hsl(var(--accent))" strokeWidth="2.5" />
                </svg>
              </div>

              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {milestones.map((w) => {
                  const lb = projectedWeight(startLb, medInfo.target, medInfo.weeks, w);
                  const disp = unit === "lb" ? lb : lb / 2.20462;
                  return (
                    <div key={w} className="rounded-xl bg-muted/40 p-3">
                      <p className="text-xs uppercase tracking-[0.18em] text-foreground/60">
                        Week {w}
                      </p>
                      <p className="font-display text-lg mt-1">
                        {disp.toFixed(1)} {unit}
                      </p>
                      <p className="text-xs text-foreground/60">
                        −{(((startLb - lb) / startLb) * 100).toFixed(1)}%
                      </p>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={PORTAL_URL}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-foreground text-background px-6 py-3 text-sm font-medium hover:opacity-90 transition"
                >
                  Start your intake <ArrowRight className="size-4" />
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-border/70 px-6 py-3 text-sm font-medium hover:bg-muted/40 transition"
                >
                  Back to Olive
                </Link>
              </div>

              <p className="mt-6 flex items-start gap-2 text-xs text-foreground/60 leading-relaxed">
                <Info className="size-3.5 mt-0.5 shrink-0" aria-hidden />
                Projections use mean clinical-trial outcomes (STEP-1 for semaglutide; SURMOUNT-1
                for tirzepatide). Individual results vary by adherence, dose titration, diet,
                activity, and physiology. Not medical advice.
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
